import CustomTimePicker from '@/components/calendar/CustomTimePicker';
import { ReactDayPickerProps } from '@/components/calendar/ReactDayPicker';
import { ReactDayPickerRange } from '@/components/calendar/ReactDayPickerRange';
import { ButtonCus } from '@/components/common/Button';
import { DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { cn } from '@/utils/utils';
import { addHours, addMonths, format, isAfter } from 'date-fns';
import { MoveRight } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { Children, cloneElement, useState } from 'react';
import { NavProps } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

type CalendarProps = ReactDayPickerProps & {
    mode: 'single' | 'multiple' | 'range',
    selected: any,
    onSelect: any,
    placeholder?: string,
    useIconRemove?: boolean,
    calendarDisabled?: boolean
};

export default observer(function RentalPeriodModal({
    mode,
    selected,
    onSelect,
    placeholder = 'placeholder.select_date',
    useIconRemove = true,
    calendarDisabled = false,
    ...props
}: CalendarProps) {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        modalStore: { hideAllModals }
    } = useStore();

    // state
    const [tempDateRange, setTempDateRange] = React.useState<any>(selected || {});
    const [tempRentalTime, setTempRentalTime] = React.useState<string>('00:00');
    const [tempReturnTime, setTempReturnTime] = React.useState<string>('23:00');

    // functions
    const CalendarChildren = () => {
        const limitMonth = addMonths(new Date(), 6);
        const [fromMonth, setFromMonth] = useState<Date>(
            tempDateRange?.from ?? tempDateRange?.to ?? new Date()
        );
        const [disableDates] = useState([
            { before: addHours(new Date(), 48) },
            { after: limitMonth }
        ]);
        const [earliestMonth] = useState(addMonths(new Date(), 0).getMonth());

        return (
            <ReactDayPickerRange
                // mode={mode}
                mode='range'
                className='max-w-full w-full xsm:!p-1 !p-1 shadow-none'
                showOutsideDays={true}
                selected={tempDateRange}
                onSelect={(date) => {
                    if (!date || !date?.from) {
                        setTempDateRange({});
                        return;
                    }
                    setTempDateRange({
                        from: date.from,
                        to: date.to
                    });
                    if (date.from) {
                        setFromMonth(date.from);
                    }
                }}
                month={fromMonth}
                onMonthChange={setFromMonth}
                modifiersClassNames={{
                    booked: 'day-booked',
                    blocked: 'day-blocked'
                }}
                components={{
                    Nav: ({ children, className, ...props }: NavProps) => {
                        const [previousButton, nextButton] = Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;
                        const prevWithClass = cloneElement(previousButton, {
                            disabled: fromMonth.getMonth() <= earliestMonth && fromMonth.getFullYear() === (new Date()).getFullYear()
                        });
                        const nextWithClass = cloneElement(nextButton, {
                            disabled: isAfter(fromMonth, limitMonth) || (fromMonth.getFullYear() === limitMonth.getFullYear() && fromMonth.getMonth() >= limitMonth.getMonth())
                        });

                        return (
                            <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                                {prevWithClass}
                                {nextWithClass}
                            </nav>
                        );
                    }
                }}
                disabled={disableDates}
                captionLayout='label'
                fixedWeeks
                classNames={{
                    day_button: 'h-7 w-7',
                    weekday: 'text-border-input-2 rounded-md w-7 font-medium text-[10px]'
                }}
            />
        );
    };

    const handleConfirm = () => {
        onSelect({
            from: tempDateRange?.from ?? new Date(),
            to: tempDateRange?.to ?? new Date(),
            rentalTime: tempRentalTime,
            returnTime: tempReturnTime
        });
        hideAllModals();
    };

    return (
        <div className=''>
            <CalendarChildren />
            <div className='flex my-3 justify-between items-center'>
                <div className='border rounded-md px-1.5 pt-1'>
                    <label className='text-[#4A4A4A] text-fs-mid13'>
                        開始時刻：
                    </label>
                    <CustomTimePicker
                        className='w-[100px] text-[15px] border-none bg-white h-9 pl-0'
                        value={tempRentalTime}
                        onChange={newTime => setTempRentalTime(newTime)}
                        disableChangeMinute={true}
                    />
                </div>
                <div className='h-6 w-6 flex-shrink-0 rounded-full flex justify-center items-center border border-[#C1C1C1]'>
                    <MoveRight className='text-[#434242] w-3 h-3' />
                </div>
                <div className='border rounded-md px-1.5 pt-1'>
                    <label className='text-[#4A4A4A] text-fs-mid13'>
                        返却時刻：
                    </label>
                    <CustomTimePicker
                        className='w-[100px] text-[15px] border-none bg-white h-9 pl-0'
                        value={tempReturnTime}
                        onChange={newTime => setTempReturnTime(newTime)}
                        disableChangeMinute={true}
                    />
                </div>
            </div>
            <div className=''>
                <div>
                    {
                        tempDateRange?.from && (
                            <span className='text-[15px] text-text'>
                                {`${format(tempDateRange.from, DATE_YMD)} ${tempRentalTime} - ${tempDateRange.to ? format(tempDateRange.to, DATE_YMD) : ''} ${tempReturnTime}`}
                            </span>
                        )
                    }
                </div>
                <div className='flex justify-center items-center mt-3'>
                    <ButtonCus
                        disabled={!tempDateRange?.from}
                        variant='default'
                        className='w-full px-10 h-9.5'
                        onClick={handleConfirm}
                    >
                        {t('button.apply')}
                    </ButtonCus>
                </div>
            </div>
        </div>
    );
});
